import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  acceptCreatorLicense,
  getCreatorLicense,
} from "../services/creatorApi";
import {
  getCandidateDisplayName,
  isCandidateLoggedIn,
  signOutCandidate,
} from "../utils/candidateAuth";
import CareersFooter from "../components/CareersFooter";
import { linkifyLegalText } from "../utils/linkifyLegalText";
import "./creatorLicense.scss";

function isSafeNext(path) {
  return (
    typeof path === "string" &&
    (path.startsWith("/me/creator") || path === "/creator" || path === "/")
  );
}

export default function CreatorLicensePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = isSafeNext(searchParams.get("next"))
    ? searchParams.get("next")
    : "/me/creator";

  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const loggedIn = isCandidateLoggedIn();
  const displayName = getCandidateDisplayName();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreatorLicense()
      .then((data) => {
        if (!cancelled) {
          setLicense(data.license || null);
          setError("");
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load the license.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onAccept = async () => {
    if (!checked || !license?.version) return;
    setBusy(true);
    setError("");
    try {
      await acceptCreatorLicense(license.version);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err.message || "Could not save acceptance.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="creator-license-page">
      <Helmet>
        <title>Creator Content License | QikTruck Careers</title>
        <meta
          name="description"
          content="QikTruck Creator Content License. Non-exclusive rights to edit, AI-edit uploaded footage, reuse, and commercialise approved creator content."
        />
      </Helmet>

      <header className="creator-license-top">
        <Link to="/creator" className="creator-license-brand">
          QikTruck Creator
        </Link>
        <button
          type="button"
          className="creator-license-menu-btn"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          Menu
        </button>
        <nav className={`creator-license-nav${menuOpen ? " is-open" : ""}`}>
          <Link to="/creator">Creator home</Link>
          <Link to="/creator/privacy">Privacy notice</Link>
          {loggedIn ? (
            <>
              <Link to="/me/creator">Scripts</Link>
              <button
                type="button"
                onClick={() => {
                  signOutCandidate();
                  navigate("/creator");
                }}
              >
                Sign out{displayName ? ` (${displayName})` : ""}
              </button>
            </>
          ) : (
            <Link to={`/login?next=${encodeURIComponent("/creator/license")}`}>
              Sign in
            </Link>
          )}
        </nav>
      </header>

      <main className="creator-license-main">
        <p className="creator-license-kicker">Legal</p>
        <h1>{license?.title || "QikTruck Creator Content License"}</h1>
        {license?.version ? (
          <p className="creator-license-version">Version {license.version}</p>
        ) : null}

        {loading ? <p className="creator-license-status">Loading…</p> : null}
        {error ? <p className="creator-license-error">{error}</p> : null}

        {!loading && license ? (
          <>
            <div className="creator-license-body">
              {(license.sections || []).map((section) => (
                <section key={section.id} className="creator-license-section">
                  <h2>{section.title}</h2>
                  {(section.body || []).map((paragraph, index) => (
                    <p key={`${section.id}-${index}`}>
                      {linkifyLegalText(paragraph)}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            {loggedIn ? (
              <div className="creator-license-accept">
                {license.accepted ? (
                  <p className="creator-license-accepted">
                    You accepted version {license.accepted_version || license.version}
                    {license.accepted_at
                      ? ` on ${new Date(license.accepted_at).toLocaleString("en-ZA")}`
                      : ""}
                    .
                  </p>
                ) : (
                  <>
                    <label className="creator-license-check">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => setChecked(event.target.checked)}
                      />
                      <span>
                        I have read this Creator Content License and grant QikTruck
                        the non-exclusive rights described, including edit, AI edit
                        of uploaded footage, reuse, repost, and commercial use of
                        approved Content for profit.
                      </span>
                    </label>
                    <button
                      type="button"
                      className="creator-license-accept__btn"
                      disabled={!checked || busy}
                      onClick={onAccept}
                    >
                      {busy ? "Saving…" : "Accept and continue"}
                    </button>
                  </>
                )}
                <Link className="creator-license-back" to={nextPath}>
                  Back to Creator
                </Link>
              </div>
            ) : (
              <p className="creator-license-signin">
                <Link to={`/login?next=${encodeURIComponent(`/creator/license?next=${encodeURIComponent(nextPath)}`)}`}>
                  Sign in
                </Link>{" "}
                to accept this license before you start a script.
              </p>
            )}
          </>
        ) : null}
      </main>

      <CareersFooter />
    </div>
  );
}
